import { ref } from "vue";

/**
 * Sections that play by themselves (PRO-4397): the memory book turns its pages and the slideshow
 * plays silently, once they have been on screen for a moment. They pause off screen and in a
 * hidden tab, stop for good at the first touch, and never run with reduced motion.
 */

/** Seen this long (and 60 % in view) before anything starts. */
export const AUTO_DELAY_MS = 2000;
/** The book: one spread every … */
export const BOOK_STEP_MS = 3800;
/** Back in view while playing: the next step comes after … */
const RESUME_MS = 1200;
const SEEN = 0.6;

/** Sections play by themselves on phones too, stopping at the first touch; never with reduced motion. */
export function autoplayAllowed(): boolean {
  return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Calls back with whether at least 60 % of `el` is on screen in a visible tab, whenever that changes. */
export function watchSeen(
  el: Element,
  callback: (seen: boolean) => void
): () => void {
  let seen = false;
  const report = () => callback(seen && !document.hidden);
  const observer = new IntersectionObserver(
    ([entry]) => {
      seen = (entry?.intersectionRatio ?? 0) >= SEEN;
      report();
    },
    { threshold: [0, SEEN, 1] }
  );
  observer.observe(el);
  document.addEventListener("visibilitychange", report);
  return () => {
    observer.disconnect();
    document.removeEventListener("visibilitychange", report);
  };
}

export interface Turner {
  go: (to: number) => void;
  jump: (to: number) => void;
  readonly last: number;
  readonly pos: number;
}

/**
 * - `idle`: waiting to be seen (the section only)
 * - `playing`: turning; the bar shows pause
 * - `stopped`: paused by the reader or by a touch; the bar shows play
 * - `done`: reached the end by itself; the section shows nothing, the large view play
 * - `off`: stopped before it ever started; the bar shows play
 */
export type AutoState = "idle" | "playing" | "stopped" | "done" | "off";

export interface BookAutoplay {
  destroy: () => void;
  play: (delay?: number) => void;
  /** Whether the bar shows the pause/play button. */
  readonly shown: boolean;
  readonly state: AutoState;
  stop: () => void;
}

/**
 * The book turns by itself once it has been on screen for 2 s: the cover opens, then one spread
 * every 3.8 s, and after the last spread it closes on the back cover and stays there.
 * `manual` (the large view): nothing starts by itself; play is always there and starts from the
 * cover again after the end. `start`: play at once (the section was playing when the view opened).
 */
export function bookAutoplay(
  book: Turner,
  watched: Element,
  options: { allowed: boolean; manual?: boolean; start?: boolean }
): BookAutoplay {
  const manual = !!options.manual;
  const can = manual || options.allowed;
  const state = ref<AutoState>(manual ? "stopped" : "idle");
  let timer = 0;
  let inView = false;

  const clear = () => {
    window.clearTimeout(timer);
    timer = 0;
  };
  const tick = () => {
    if (state.value !== "playing" || !inView) {
      return;
    }
    if (book.pos >= book.last) {
      state.value = "done";
      return;
    }
    book.go(book.pos + 1);
    timer = window.setTimeout(tick, BOOK_STEP_MS);
  };
  const play = (delay = 600) => {
    if (!can) {
      return;
    }
    if (book.pos >= book.last) {
      book.jump(0);
    }
    state.value = "playing";
    clear();
    timer = window.setTimeout(tick, delay);
  };
  const stop = () => {
    if (state.value === "playing" || state.value === "idle") {
      clear();
      state.value = state.value === "idle" ? "off" : "stopped";
    }
  };

  const unwatch = watchSeen(watched, (seen) => {
    inView = seen;
    if (!can) {
      return;
    }
    if (seen && state.value === "idle") {
      clear();
      timer = window.setTimeout(() => {
        state.value = "playing";
        tick();
      }, AUTO_DELAY_MS);
    } else if (seen && state.value === "playing") {
      clear();
      timer = window.setTimeout(tick, RESUME_MS);
    } else if (!seen && (state.value === "idle" || state.value === "playing")) {
      clear();
    }
  });

  if (options.start) {
    play(RESUME_MS);
  }

  return {
    destroy() {
      unwatch();
      clear();
    },
    play,
    get shown() {
      if (!can || state.value === "idle") {
        return false;
      }
      return manual || state.value !== "done";
    },
    get state() {
      return state.value;
    },
    stop,
  };
}
